import "./ColorPickerComponent.css";
import { FC, useMemo } from "react";

interface ColorPickerInterface {
    colorPicked(color: string): void;
    selectedColor?: string;
}

// Generate HSL color
const hslToHex = (h: number, s: number, l: number): string => {
    s /= 100;
    l /= 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color)
            .toString(16)
            .padStart(2, "0");
    };
    return `#${f(0)}${f(8)}${f(4)}`;
};

const ColorPickerComponent: FC<ColorPickerInterface> = ({
    colorPicked,
    selectedColor,
}) => {
    const colors = useMemo(() => {
        const grid: string[] = [];
        const cols = 10;

        // First row: grayscale from white to black
        for (let i = 0; i < cols; i++) {
            const lightness = 100 - (i * 100) / (cols - 1);
            grid.push(hslToHex(0, 0, lightness));
        }

        // Remaining rows: color spectrum
        const rows = 9;
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                // Hue goes from 240 (blue) through 360/0 (red) to 120 (green)
                const hue = (240 + col * 36) % 360;
                // Saturation varies by row
                const saturation = 100 - row * 5;
                // Lightness varies by row
                const lightness = 30 + row * 7;
                grid.push(hslToHex(hue, saturation, lightness));
            }
        }

        return grid;
    }, []);

    const isSelected = (color: string) => {
        return selectedColor?.toLowerCase() === color.toLowerCase();
    };

    return (
        <div className="color-picker-container">
            <div className="color-picker-grid">
                {colors.map((color, index) => (
                    <button
                        key={index}
                        className={`color-picker-cell ${isSelected(color) ? "selected" : ""}`}
                        style={{ backgroundColor: color }}
                        onClick={() => colorPicked(color)}
                    />
                ))}
            </div>
        </div>
    );
};

export default ColorPickerComponent;
